import { getDealTimeToBuyBadgePresentation } from '@/components/deal/privateDeal/constants/DealPrivateBadgeColor.constant';
import type {
  PipelineBoardTableRowView,
  PipelineDealCardViewRecord,
  PipelineKanbanColumnInput,
  PipelineKanbanColumnState,
  PipelineKanbanColumnView,
} from '@/modules/pipeline/types/Pipeline.types';
import type {
  SearchPipelineByIdResponse,
  StageItemsRecord,
  StageWithItems,
} from '@/modules/pipeline/types/PipelineAPI.types';
import systemConstants from '@/settings/systemConstants';
import { formatBrlCompact } from '@/utils/currencyHelpers';
import { useToCurrency } from '@/utils/useToCurrency';

const DEFAULT_ORIGIN_LABEL = 'Cadastro público';
const DEFAULT_ORIGIN_BADGE_CLASS =
  'border-emerald-500/50 bg-emerald-500/10 text-[10px] font-medium text-emerald-500';
const DEFAULT_SEMAPHORE_BADGE_CLASS = 'bg-zinc-600 hover:bg-zinc-500';

const getLeadScorePresentation = (
  score: number | null | undefined,
): Pick<PipelineDealCardViewRecord, 'leadScoreLabel' | 'leadScoreClass'> => {
  if (score === null || score === undefined) {
    return {
      leadScoreLabel: '---',
      leadScoreClass: 'text-zinc-500',
    };
  }

  if (score >= 700) {
    return {
      leadScoreLabel: score,
      leadScoreClass: 'text-green-500',
    };
  }

  if (score >= 600) {
    return {
      leadScoreLabel: score,
      leadScoreClass: 'text-blue-500',
    };
  }

  if (score >= 500) {
    return {
      leadScoreLabel: score,
      leadScoreClass: 'text-yellow-500',
    };
  }

  return {
    leadScoreLabel: score,
    leadScoreClass: 'text-red-500',
  };
};

const getAssetSegmentLabel = (
  segment: StageItemsRecord['assetSegment'],
): string => {
  if (segment === 'Realty') return 'Imóvel';
  if (segment === 'Vehicle') return 'Veículo';
  return '';
};

const getOriginPresentation = (
  origin: StageItemsRecord['origin'],
): Pick<
  PipelineDealCardViewRecord,
  'originLabel' | 'originBadgeClass' | 'showOriginBadge'
> => {
  const originLabel =
    systemConstants.dealOriginOptions.find((item) => item.value === origin)
      ?.label ?? DEFAULT_ORIGIN_LABEL;

  if (originLabel.toLowerCase().includes('intern')) {
    return {
      originLabel,
      originBadgeClass:
        'border-blue-500/50 bg-blue-500/10 text-[10px] font-medium text-blue-500',
      showOriginBadge: true,
    };
  }

  return {
    originLabel,
    originBadgeClass: DEFAULT_ORIGIN_BADGE_CLASS,
    showOriginBadge: true,
  };
};

const getSemaphorePresentation = (
  flag: StageItemsRecord['stageSemaphoreFlag'],
): Pick<
  PipelineDealCardViewRecord,
  'semaphoreLabel' | 'semaphoreBadgeClass' | 'showSemaphore'
> => {
  const semaphore = systemConstants.StageSemaphoreFlagEnum[flag];

  if (semaphore === 'GREEN') {
    return {
      semaphoreLabel: 'No prazo',
      semaphoreBadgeClass: 'bg-green-500 hover:bg-green-600',
      showSemaphore: true,
    };
  }

  if (semaphore === 'YELLOW') {
    return {
      semaphoreLabel: 'Próximo do vencimento',
      semaphoreBadgeClass: 'bg-yellow-500 hover:bg-yellow-600',
      showSemaphore: true,
    };
  }

  if (semaphore === 'RED') {
    return {
      semaphoreLabel: 'Em atraso',
      semaphoreBadgeClass: 'bg-red-500 hover:bg-red-600',
      showSemaphore: true,
    };
  }

  return {
    semaphoreLabel: '',
    semaphoreBadgeClass: DEFAULT_SEMAPHORE_BADGE_CLASS,
    showSemaphore: false,
  };
};

const getTimeToBuyPresentation = (
  timeToBuy: StageItemsRecord['timeToBuy'],
): Pick<
  PipelineDealCardViewRecord,
  'timeToBuyLabel' | 'timeToBuyBadgeClass' | 'timeToBuyIcon'
> => {
  const presentation = getDealTimeToBuyBadgePresentation(timeToBuy);
  if (presentation) {
    return {
      timeToBuyLabel: presentation.label,
      timeToBuyBadgeClass: presentation.class,
      timeToBuyIcon: presentation.icon,
    };
  }

  return {
    timeToBuyLabel: '',
    timeToBuyBadgeClass: '',
    timeToBuyIcon: null,
  };
};

export const buildPipelineDealCardViewRecord = (
  deal: StageItemsRecord,
): PipelineDealCardViewRecord => {
  const leadScorePresentation = getLeadScorePresentation(deal.leadScore);
  const originPresentation = getOriginPresentation(deal.origin);
  const semaphorePresentation = getSemaphorePresentation(
    deal.stageSemaphoreFlag,
  );
  const timeToBuyPresentation = getTimeToBuyPresentation(deal.timeToBuy);

  return {
    id: deal.id,
    customerName: deal.customer?.name ?? 'Negócio sem nome',
    ariaLabel: `Negócio de ${deal.customer?.name ?? 'cliente'}`,
    operationValue: deal.operationValue,
    timeToBuy: deal.timeToBuy,
    ...timeToBuyPresentation,
    operationValueLabel: useToCurrency(deal.operationValue),
    assetSegmentLabel: getAssetSegmentLabel(deal.assetSegment),
    assetValueLabel: useToCurrency(deal.asset?.value),
    ownerLabel: deal.user?.name ?? '',
    organizationLabel: deal.subsidiary?.name ?? deal.company?.name ?? '',
    isComplete: deal.isComplete,
    registrationLabel: deal.isComplete ? 'Completo' : 'Incompleto',
    registrationStatusClass: deal.isComplete
      ? 'text-emerald-400'
      : 'text-red-400',
    fileQuantity: deal.fileQuantity ?? 0,
    showFilesIndicator: (deal.fileQuantity ?? 0) > 1,
    ...leadScorePresentation,
    ...originPresentation,
    ...semaphorePresentation,
  };
};

export const buildPipelineKanbanColumnState = (
  stage: StageWithItems,
): PipelineKanbanColumnState => {
  const cards = stage.items.map(buildPipelineDealCardViewRecord);

  return {
    id: stage.id,
    name: stage.name,
    probability: stage.probability ?? 0,
    color: stage.color ?? null,
    totalItems: stage.totalItems ?? 0,
    totalAmount: stage.totalAmount ?? 0,
    cards,
    hasMore: cards.length < (stage.totalItems ?? 0),
    isLoadingMore: false,
  };
};

export const normalizePipelineKanbanColumnState = (
  column: PipelineKanbanColumnInput,
): PipelineKanbanColumnState => {
  return {
    id: column.id,
    name: column.name,
    probability: column.probability,
    color: column.color,
    totalItems: column.totalItems,
    totalAmount: column.totalAmount,
    cards: column.cards.map((card) => ({ ...card })),
    hasMore: column.hasMore,
    isLoadingMore: column.isLoadingMore,
  };
};

export const buildPipelineKanbanColumnView = (
  column: PipelineKanbanColumnInput,
): PipelineKanbanColumnView => {
  const normalizedColumn = normalizePipelineKanbanColumnState(column);

  return {
    ...normalizedColumn,
    totalAmountLabel: formatBrlCompact(normalizedColumn.totalAmount / 100),
  };
};

export const buildPipelineKanbanColumnsState = (
  response: SearchPipelineByIdResponse,
): PipelineKanbanColumnState[] => {
  return [...response.stages]
    .sort((left, right) => (left.order ?? 0) - (right.order ?? 0))
    .map(buildPipelineKanbanColumnState);
};

export const buildPipelineKanbanColumnViews = (
  columns: PipelineKanbanColumnInput[],
): PipelineKanbanColumnView[] => {
  return columns.map(buildPipelineKanbanColumnView);
};

export const buildPipelineBoardTableRowViewRecord = (
  deal: StageItemsRecord,
  stage?: Pick<
    PipelineKanbanColumnState,
    'id' | 'name' | 'color' | 'probability'
  >,
): PipelineBoardTableRowView => {
  return {
    ...buildPipelineDealCardViewRecord(deal),
    stageId: deal.stage?.id ?? stage?.id ?? '',
    stageName: deal.stage?.name ?? stage?.name ?? 'Sem etapa',
    stageColor: stage?.color ?? null,
    stageProbability: stage?.probability ?? 0,
  };
};

export const buildPipelineBoardTableRowsFromDeals = (
  deals: StageItemsRecord[],
  columns: Array<
    Pick<PipelineKanbanColumnState, 'id' | 'name' | 'color' | 'probability'>
  >,
): PipelineBoardTableRowView[] => {
  const stageById = Object.fromEntries(
    columns.map((column) => [column.id, column]),
  );

  return deals.map((deal) =>
    buildPipelineBoardTableRowViewRecord(
      deal,
      deal.stage?.id ? stageById[deal.stage.id] : undefined,
    ),
  );
};

export const buildPipelineBoardTableRows = (
  columns: Array<
    Pick<
      PipelineKanbanColumnState,
      'id' | 'name' | 'color' | 'probability' | 'cards'
    >
  >,
): PipelineBoardTableRowView[] => {
  return columns.flatMap((column) =>
    column.cards.map((card) => ({
      ...card,
      stageId: column.id,
      stageName: column.name,
      stageColor: column.color,
      stageProbability: column.probability,
    })),
  );
};
